/**
 * Unified SignalR Context - Single Source of Truth
 * 
 * This context provides a unified approach to SignalR management across the entire
 * application. It replaces multiple competing implementations with a single,
 * centralized solution.
 * 
 * Features:
 * - Single SignalR connection per application instance
 * - Centralized authentication handling
 * - Event deduplication and reliable message handling
 * - React context for easy consumption across components
 * - Automatic reconnection and error handling
 * - Performance monitoring and debugging
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from '../context/AuthContext';
import { signalRService } from '../services/signalRService';
import { signalRManager } from '../services/SignalRManager';

export interface SignalRMessage {
  type: 'status' | 'stream' | 'complete' | 'updateSession' | 'runsStatus' | 'errorUpdate' | 'update' | 'activeRunsBlocked';
  data: any;
  timestamp: number;
}

export interface SignalRContextType {
  // Connection state
  isConnected: boolean;
  isInitialized: boolean;
  isReconnecting: boolean;
  connectionState: string;
  error: string | null;
  
  // Event subscription
  subscribe: (eventType: string, handler: (...args: any[]) => void) => () => void;
  unsubscribe: (eventType: string, handler: (...args: any[]) => void) => void;
  
  // Manual connection management
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  retry: () => Promise<void>;
  
  // Session management
  setSessionContext: (sessionId: string | null) => void;
  clearSessionContext: () => void;
  getSessionContext: () => any;
  
  // Statistics and debugging
  getStats: () => any;
  getPerformanceMetrics: () => any;
  
  // Recent messages for debugging
  recentMessages: SignalRMessage[];
}

interface UnifiedSignalRProviderProps {
  children: ReactNode;
}

const UnifiedSignalRContext = createContext<SignalRContextType | undefined>(undefined);

export const UnifiedSignalRProvider: React.FC<UnifiedSignalRProviderProps> = ({ children }) => {
  const { isAuthenticated, user, getAccessToken } = useAuth();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionState, setConnectionState] = useState('Disconnected');
  const [error, setError] = useState<string | null>(null);
  const [recentMessages, setRecentMessages] = useState<SignalRMessage[]>([]);
  
  // Prevent multiple initializations
  const initializationRef = useRef<Promise<void> | null>(null);
  const isInitializingRef = useRef(false);
  
  // Track window focus/visibility state
  const lastDisconnectTimeRef = useRef<number | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isReconnectingRef = useRef(false);
  
  // Connection status monitoring
  useEffect(() => {
    const checkConnection = () => {
      const connected = signalRService.isConnected();
      const debugInfo = signalRService.getDebugInfo();
      
      const wasConnected = isConnected;
      setIsConnected(connected);
      setConnectionState(debugInfo.connectionState);
      
      // Track when we lose connection
      if (wasConnected && !connected) {
        lastDisconnectTimeRef.current = Date.now();
      }
    };

    checkConnection();
    const interval = setInterval(checkConnection, 2000);
    
    return () => clearInterval(interval);
  }, [isConnected]);

  // Handle window focus/visibility events for automatic reconnection
  // This solves the issue where SignalR connection drops after computer sleep
  // or browser inactivity and doesn't automatically reconnect
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated && user?.id && !isConnected && !isReconnectingRef.current) {
        const timeSinceDisconnect = lastDisconnectTimeRef.current ? Date.now() - lastDisconnectTimeRef.current : 0;
        
        // Only reconnect if we've been disconnected for more than 5 seconds
        // This prevents unnecessary reconnection attempts
        if (timeSinceDisconnect > 5000) {
          console.log('[UnifiedSignalR] Page became visible and connection lost, attempting reconnection...');
          attemptReconnection();
        }
      }
    };

    const handleFocus = () => {
      if (isAuthenticated && user?.id && !isConnected && !isReconnectingRef.current) {
        const timeSinceDisconnect = lastDisconnectTimeRef.current ? Date.now() - lastDisconnectTimeRef.current : 0;
        
        // Only reconnect if we've been disconnected for more than 5 seconds
        if (timeSinceDisconnect > 5000) {
          console.log('[UnifiedSignalR] Window gained focus and connection lost, attempting reconnection...');
          attemptReconnection();
        }
      }
    };

    const attemptReconnection = async () => {
      if (isReconnectingRef.current || isInitializingRef.current) return;
      
      isReconnectingRef.current = true;
      setIsReconnecting(true);
      
      try {
        console.log('[UnifiedSignalR] Attempting to reconnect after inactivity...');
        setError(null);
        
        const accessToken = await getAccessToken();
        if (!accessToken) {
          throw new Error('No access token available');
        }

        // Initialize the service (this includes joining user session)
        await signalRService.initialize(accessToken, user.id);
        
        // Initialize the manager
        await signalRManager.initialize();
        
        // Explicitly rejoin user session to ensure we're in the SignalR group
        // This is critical for receiving messages after reconnection
        try {
          await signalRService.joinUserSession(user.id);
          console.log('[UnifiedSignalR] Successfully rejoined user session after reconnection');
        } catch (joinError) {
          console.warn('[UnifiedSignalR] Failed to rejoin user session, will retry:', joinError);
        }
        
        // Restore session context if we had one
        const sessionContext = signalRManager.getSessionContext();
        if (sessionContext?.sessionId) {
          signalRManager.setSessionContext(sessionContext.sessionId, user.id);
        }
        
        console.log('[UnifiedSignalR] Reconnection successful after inactivity');
        lastDisconnectTimeRef.current = null;
        
      } catch (error) {
        console.error('[UnifiedSignalR] Failed to reconnect after inactivity:', error);
        setError(`Failed to reconnect: ${error instanceof Error ? error.message : 'Unknown error'}`);
        
        // Schedule retry in 10 seconds if we're still authenticated
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isAuthenticated && user?.id && !isConnected) {
            attemptReconnection();
          }
        }, 10000);
        
      } finally {
        isReconnectingRef.current = false;
        setIsReconnecting(false);
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      
      // Clear any pending reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, isConnected, getAccessToken]);

  // Clear reconnection timeout on cleanup
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, []);

  // Initialize SignalR when user is authenticated
  useEffect(() => {
    const initializeSignalR = async () => {
      if (!isAuthenticated || !user?.id || isInitializingRef.current) return;
      
      // Prevent multiple concurrent initializations
      if (initializationRef.current) {
        await initializationRef.current;
        return;
      }

      isInitializingRef.current = true;
      setError(null);

      try {
        // console.log('[UnifiedSignalR] Initializing SignalR for user:', user.id);
        
        const accessToken = await getAccessToken();
        if (!accessToken) {
          throw new Error('No access token available');
        }

        // Initialize the service first
        initializationRef.current = signalRService.initialize(accessToken, user.id);
        await initializationRef.current;

        // Initialize the manager
        await signalRManager.initialize();
        
        // Set initial session context with user info
        signalRManager.setSessionContext(null, user.id);
        
        setIsInitialized(true);
        // console.log('[UnifiedSignalR] SignalR initialized successfully');
        
      } catch (error) {
        console.error('[UnifiedSignalR] Failed to initialize SignalR:', error);
        setError(`Failed to initialize SignalR: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        isInitializingRef.current = false;
        initializationRef.current = null;
      }
    };

    initializeSignalR();
  }, [isAuthenticated, user?.id, getAccessToken]);

  // Cleanup when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      setIsInitialized(false);
      setIsConnected(false);
      setIsReconnecting(false);
      setConnectionState('Disconnected');
      setError(null);
      setRecentMessages([]);
      isInitializingRef.current = false;
      isReconnectingRef.current = false;
      initializationRef.current = null;
      lastDisconnectTimeRef.current = null;
      
      // Clear any pending reconnection timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Clear session context
      signalRManager.clearSessionContext();
    }
  }, [isAuthenticated]);

  // Track recent messages for debugging
  const addRecentMessage = useCallback((type: SignalRMessage['type'], data: any) => {
    const message: SignalRMessage = {
      type,
      data,
      timestamp: Date.now()
    };
    
    setRecentMessages(prev => {
      const newMessages = [message, ...prev.slice(0, 49)]; // Keep last 50 messages
      return newMessages;
    });
  }, []);

  // Event subscription methods
  const subscribe = useCallback((eventType: string, handler: (...args: any[]) => void) => {
    // console.log(`[UnifiedSignalR] Subscribing to event: ${eventType}`);
    
    // Wrap handler to track messages
    const wrappedHandler = (...args: any[]) => {
      addRecentMessage(eventType as SignalRMessage['type'], args);
      handler(...args);
    };
    
    return signalRManager.subscribe(eventType, wrappedHandler);
  }, [addRecentMessage]);

  const unsubscribe = useCallback((eventType: string, handler: (...args: any[]) => void) => {
    // console.log(`[UnifiedSignalR] Unsubscribing from event: ${eventType}`);
    signalRManager.off(eventType, handler);
  }, []);

  // Manual connection management
  const connect = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      throw new Error('User not authenticated');
    }

    const accessToken = await getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    await signalRService.initialize(accessToken, user.id);
    await signalRManager.initialize();
  }, [isAuthenticated, user?.id, getAccessToken]);

  const disconnect = useCallback(async () => {
    await signalRService.disconnect();
    setIsConnected(false);
    setConnectionState('Disconnected');
  }, []);

  const retry = useCallback(async () => {
    if (isReconnectingRef.current || isInitializingRef.current) {
      console.log('[UnifiedSignalR] Retry skipped - already in progress');
      return;
    }
    
    setError(null);
    lastDisconnectTimeRef.current = null; // Clear disconnect timestamp
    await connect();
  }, [connect]);

  // Get statistics and performance metrics
  const getStats = useCallback(() => {
    return {
      ...signalRManager.getStats(),
      debugInfo: signalRService.getDebugInfo(),
      recentMessageCount: recentMessages.length
    };
  }, [recentMessages.length]);

  const getPerformanceMetrics = useCallback(() => {
    return signalRManager.getPerformanceMetrics();
  }, []);

  // Session management methods
  const setSessionContext = useCallback((sessionId: string | null) => {
    signalRManager.setSessionContext(sessionId, user?.id || null);
  }, [user?.id]);

  const clearSessionContext = useCallback(() => {
    signalRManager.clearSessionContext();
  }, []);

  const getSessionContext = useCallback(() => {
    return signalRManager.getSessionContext();
  }, []);

  const value: SignalRContextType = {
    // Connection state
    isConnected,
    isInitialized,
    isReconnecting,
    connectionState,
    error,
    
    // Event subscription
    subscribe,
    unsubscribe,
    
    // Manual connection management
    connect,
    disconnect,
    retry,
    
    // Session management
    setSessionContext,
    clearSessionContext,
    getSessionContext,
    
    // Statistics and debugging
    getStats,
    getPerformanceMetrics,
    recentMessages
  };

  return (
    <UnifiedSignalRContext.Provider value={value}>
      {children}
    </UnifiedSignalRContext.Provider>
  );
};

export const useUnifiedSignalR = (): SignalRContextType => {
  const context = useContext(UnifiedSignalRContext);
  if (!context) {
    throw new Error('useUnifiedSignalR must be used within a UnifiedSignalRProvider');
  }
  return context;
};

// Convenience hook for event subscriptions with automatic cleanup
export const useSignalREvent = (
  eventType: string, 
  handler: (...args: any[]) => void,
  dependencies: any[] = []
) => {
  const { subscribe } = useUnifiedSignalR();
  
  useEffect(() => {
    const unsubscribe = subscribe(eventType, handler);
    return unsubscribe;
  }, [subscribe, eventType, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps
};

// Hook for multiple event subscriptions
export const useSignalREvents = (
  eventHandlers: Record<string, (...args: any[]) => void>,
  dependencies: any[] = []
) => {
  const { subscribe } = useUnifiedSignalR();
  
  useEffect(() => {
    console.log('[useSignalREvents] Setting up event handlers for events:', Object.keys(eventHandlers));
    
    const unsubscribers = Object.entries(eventHandlers).map(([eventType, handler]) => {
      console.log(`[useSignalREvents] Subscribing to: ${eventType}`);
      return subscribe(eventType, handler);
    });
    
    return () => {
      console.log('[useSignalREvents] Cleaning up event handlers for events:', Object.keys(eventHandlers));
      unsubscribers.forEach(unsub => unsub());
    };
  }, [subscribe, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps
};
